import { gql } from '@apollo/client';
import { StatusBadge } from 'modules/experiments/components/ui';
import { useExperimentTakeover } from 'modules/experiments/takeover';
import { ExperimentTakeoverSource } from 'modules/experiments/takeover/constants';
import { CampaignStateDefinition } from 'modules/graphql';
import {
  FormBodyText,
  FormHeadingText,
  WrappingStyledLinkWithIcon,
} from 'modules/workflowNodeEditors/EditorBody.styled';
import { Experiment } from 'modules/workflows/types';
import { OpenNewWindow16 } from 'ui/icons';
import { useGraphql } from 'utils/hooks/useGraphql';
import { TextContainer, TextWrapper } from './ExperimentSection.styled';
import { NewExperimentButton } from './NewExperimentButton';

export const EXPERIMENT_LINK = 'experiment_link';

const content = {
  nameHeading: 'Name: ',
  variantHeading: 'Variants: ',
  statusHeading: 'Status: ',
  editLinkLabel: 'Edit A/B experiment',
  createLinkLabel: 'Create New A/B experiment',
  analyticsLink: 'View analytics',
};

export const fetchExperimentVariationsQuery = gql`
  query fetchExperimentVariations($id: Long!) {
    experiment(id: $id) {
      variations {
        id
      }
    }
  }
`;

type ExperimentSectionTextProps = {
  /** experiments */
  experiment: Experiment;
  /** Campaign Id */
  campaignId?: number;
  /** Campaign Name */
  campaignName: string;
  /** Campaign State */
  campaignState?: CampaignStateDefinition;
  /** Is the campaign's delivery set to send time optimized */
  isCampaignSTOEnabled: boolean;
  /** Is the section disabled */
  isExperimentCreationDisabled?: boolean;
  /** Disabled tooltip text */
  tooltipText: string;
};

export const ExperimentSectionText = ({
  experiment,
  campaignId,
  campaignName,
  campaignState,
  isCampaignSTOEnabled,
  isExperimentCreationDisabled,
  tooltipText,
}: ExperimentSectionTextProps) => {
  const { getExperimentLinkAttributes } = useExperimentTakeover();
  const { data } = useGraphql(fetchExperimentVariationsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { id: experiment.id },
  });
  const experimentVariations = data?.experiment?.variations || [];

  const linkProps = { rel: 'noreferrer noopener', target: '_blank' };
  const isFinished = experiment?.state === 'Finished';
  const experimentAttributes = getExperimentLinkAttributes({
    source: ExperimentTakeoverSource.WORKFLOW_NODE_EDITOR,
    experiment,
  });

  return (
    <TextContainer>
      <TextWrapper>
        <FormHeadingText>{content.nameHeading}</FormHeadingText>
        <FormBodyText>
          <WrappingStyledLinkWithIcon
            data-test={EXPERIMENT_LINK}
            {...linkProps}
            {...experimentAttributes}
          >
            {experiment.name}
            {experimentAttributes.href && <OpenNewWindow16 />}
          </WrappingStyledLinkWithIcon>
        </FormBodyText>
      </TextWrapper>
      <TextWrapper>
        <FormHeadingText>{content.variantHeading}</FormHeadingText>
        <FormBodyText>{experimentVariations?.length}</FormBodyText>
      </TextWrapper>
      <TextWrapper>
        <FormHeadingText>{content.statusHeading}</FormHeadingText>
        <FormBodyText>{experiment.state && <StatusBadge state={experiment.state} />}</FormBodyText>
      </TextWrapper>
      {isFinished && (
        <TextWrapper>
          <FormHeadingText />
          <FormBodyText>
            <NewExperimentButton
              campaignId={campaignId}
              campaignName={campaignName}
              campaignState={campaignState}
              isCampaignSTOEnabled={isCampaignSTOEnabled}
              isExperimentCreationDisabled={isExperimentCreationDisabled}
              tooltipText={tooltipText}
            />
          </FormBodyText>
        </TextWrapper>
      )}
    </TextContainer>
  );
};
